import { StringUtil } from "@base/util/StringUtil";
import { Util } from "@base/util/Util";
import { IArticle } from "@biz/definitions/ArticleDefinitions";
import { DiscountGroups } from "@biz/definitions/DatabaseDefinitions";
import { ICurrencyData, IPriceOptions } from "@biz/definitions/PriceDefinitions";
import { RuleArticleLogic } from "@biz/logic/RuleArticleLogic";
import { QuoteHelper } from "@biz/SSR/QuoteHelper";

/** @deprecated */
export function getPriceMul(): number {
  return 1000;
}

export interface IPriceCalculationContext {
  companySurcharge: number;
  customerSurcharge: number;
  currency: ICurrencyData;
}


export class PriceCalculator {
  static getFormattedCustomerPrice(articlePrice: number, _mul: number | undefined, beosArticle: IArticle | undefined, pcc: IPriceCalculationContext): string {
    if (beosArticle) {
      const mul = _mul === undefined ? 1 : _mul;
      const userPrice = PriceCalculator.getCustomerPrice(articlePrice, pcc.customerSurcharge, pcc.currency);
      const amountPrice = PriceCalculator.calcAmountPrice(userPrice, 1, mul, pcc.currency);
      return PriceCalculator.formatPrice(amountPrice, pcc.currency);
    }
    return "?"
  }

  static getFormattedCustomerPriceForBeosArticle(article: IArticle | undefined, pcc: IPriceCalculationContext, options: IPriceOptions): string {
    if (!article)
      return "?";
    const articlePrice = RuleArticleLogic.calcPricePerItem(article, pcc.companySurcharge, options);
    return PriceCalculator.getFormattedCustomerPrice(articlePrice, options.mul === undefined ? 1 : options.mul, article, pcc)
  }

  static getFormattedCustomerPriceForCustomArticle(articlePrice: number | undefined, currency: ICurrencyData): string {
    return PriceCalculator.formatPrice(articlePrice || 0, currency);
  }


  static getSalesDiscount(discountGroup: string, discountGroups: DiscountGroups | undefined): number {
    const discount = discountGroups?.[discountGroup]?.discount || 0;
    return discount;
  }

  static getDealerPriceDirect(price: number, discountValue: number, _isCustomArticle: boolean, cd: ICurrencyData): number {
    if (_isCustomArticle) return 0;
    const localPrice = price * cd.factor;
    const ek = localPrice - (discountValue ? (localPrice * -discountValue) / 100 : 0);
    return PriceCalculator.roundToVisibleDecimalPlaces(ek, cd);
  }

  static getDealerPriceWithDiscounts(price: number, discountGroups: DiscountGroups, discountGroup: string, isCustomArticle: boolean, cd: ICurrencyData): number {
    if (isCustomArticle) return 0;
    const discount = PriceCalculator.getSalesDiscount(discountGroup, discountGroups);
    return PriceCalculator.getDealerPriceDirect(price, discount, isCustomArticle, cd);
  }

  private static roundToVisibleDecimalPlaces(price: number, cd: ICurrencyData): number {
    const commaCorrection = 1 / Math.pow(10, cd.decimalPlaces);
    let p2 = price + commaCorrection / 2;
    p2 = p2 - (p2 % commaCorrection);
    return p2;
  }

  static getCustomerPrice(price: number, customerSurCharge: number, cd: ICurrencyData): number {
    customerSurCharge = customerSurCharge || 0; // old projects
    const localPrice = price * cd.factor;
    const p = localPrice + (localPrice * customerSurCharge) / 100;

    return PriceCalculator.roundToVisibleDecimalPlaces(p, cd);
  }

  static calcAmountPrice(price: number, count: number, mul: number | undefined, currencyData: ICurrencyData): number {
    let retPrice = 0;
    retPrice = price * count * Util.round(mul ?? 1, 2);
    return PriceCalculator.roundToVisibleDecimalPlaces(retPrice, currencyData);
  }

  static priceToString(value: number): number {
    return Util.round(value, 2);
  }

  private static numberWithCommas(x: string): string {
    // https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
    // return x.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ","); // look behind, doesn't work on safari, but faster than split join
    const parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }

  static calcVat(amount: number, vat: number): number {
    return (amount * vat) / 100;
  }

  static formatPrice(amount: number, currencyData: ICurrencyData): string {
    const _val = amount.toFixed(currencyData.decimalPlaces);
    let val = PriceCalculator.numberWithCommas(_val);
    // replace ".00" with ".-"
    if (val.endsWith(".00")) val = val.substring(0, val.length - 3) + ".-";

    // replace '.' with ','
    val = StringUtil.replaceAll(val, ".", "#");
    val = StringUtil.replaceAll(val, ",", ".");
    val = StringUtil.replaceAll(val, "#", ",");

    return val + QuoteHelper.nonBreakingSpace + currencyData.sign;
  }
}
